import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Close,
  Delete,
  Groups,
} from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTheme } from "../../context/ThemeContext";
import agentService from "../../services/agentService";
import styles from "./AgentTeamDialog.module.scss";

const TEAM_MODES = [
  {
    value: "coordinate",
    label: "Coordinate",
    hint: "Leader analyzes the task, picks the right members, and synthesizes their outputs into one answer. Use when steps depend on each other or need a unified response.",
  },
  {
    value: "route",
    label: "Route",
    hint: "Leader picks the single best-fit member and returns that member's response directly. Use when only one specialist should handle the request (e.g., classification-style routing).",
  },
  {
    value: "broadcast",
    label: "Broadcast",
    hint: "All members run in parallel and the leader merges their outputs. Use when you want multiple perspectives on the same input (e.g., reviews, comparisons, parallel analyses).",
  },
];

const AGENTS_PAGE_SIZE = 20;
const TEAM_MODE_VALUES = TEAM_MODES.map((mode) => mode.value);
const MAX_TEAM_NAME_LENGTH = 200;
const MAX_MEMBER_ROLE_LENGTH = 200;

const isRequiredNumber = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return Number.isFinite(Number(value));
};

const normalizeDetermineInputForMembers = (teamMode, value) => {
  if (teamMode === "broadcast") return false;
  if (value === undefined || value === null) return true;
  return Boolean(value);
};

const normalizeMembers = (members = []) =>
  [...members]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((member, index) => ({
      member_agent_id: member.member_agent_id || member.agent_id || member.id || "",
      member_name: member.member_name || member.name || member.agent_name || "",
      role: member.role || "",
      position: index,
    }));

const AgentTeamDialog = ({
  open,
  onClose,
  onSave,
  initialData,
  leaderAgentId,
  leaderName,
  saving = false,
}) => {
  const { isDarkMode } = useTheme();
  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: { main: "#e36250 !important" },
      secondary: { main: "#8b5cf6" },
    },
  });

  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentsPage, setAgentsPage] = useState(1);
  const [loadingMoreAgents, setLoadingMoreAgents] = useState(false);
  const [hasMoreAgents, setHasMoreAgents] = useState(true);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [error, setError] = useState("");
  const scrollTimeoutRef = React.useRef(null);
  const isFetchingAgentsRef = React.useRef(false);
  const activeLoadIdRef = React.useRef(0);
  const [formData, setFormData] = useState({
    team_mode: "coordinate",
    name: "",
    description: "",
    instructions: "",
    determine_input_for_members: true,
    members: [],
  });

  useEffect(() => {
    if (!open) return;
    const members = normalizeMembers(initialData?.members || []);
    const initialTeamMode = initialData?.team_mode || "coordinate";
    setFormData({
      team_mode: initialTeamMode,
      name: initialData?.name || (leaderName ? `${leaderName} Team` : ""),
      description: initialData?.description || "",
      instructions: initialData?.instructions || "",
      determine_input_for_members: normalizeDetermineInputForMembers(
        initialTeamMode,
        initialData?.determine_input_for_members
      ),
      members,
    });
    setSelectedAgentId("");
    setError("");
  }, [open, initialData, leaderName]);

  const agentById = useMemo(() => {
    const map = new Map();
    availableAgents.forEach((agent) => map.set(String(agent.id), agent));
    return map;
  }, [availableAgents]);

  const selectableAgents = useMemo(() => {
    const selectedIds = new Set(formData.members.map((member) => String(member.member_agent_id)));
    return availableAgents.filter(
      (agent) => String(agent.id) !== String(leaderAgentId) && !selectedIds.has(String(agent.id))
    );
  }, [availableAgents, formData.members, leaderAgentId]);

  const loadAgents = React.useCallback(async (pageNum = 1, append = false) => {
    if (isFetchingAgentsRef.current) return;
    isFetchingAgentsRef.current = true;
    const loadId = activeLoadIdRef.current + 1;
    activeLoadIdRef.current = loadId;

    if (append) {
      setLoadingMoreAgents(true);
    } else {
      setLoadingAgents(true);
    }

    try {
      const response = await agentService.getAgents(pageNum, AGENTS_PAGE_SIZE);
      if (activeLoadIdRef.current !== loadId) return;

      const nextAgents = response?.agents || [];

      setAvailableAgents((prev) => {
        if (!append) return nextAgents;

        const existingIds = new Set(prev.map((agent) => String(agent.id)));
        const uniqueNextAgents = nextAgents.filter((agent) => !existingIds.has(String(agent.id)));
        return [...prev, ...uniqueNextAgents];
      });

      const totalPages = Number(response?.total_pages) || 0;
      setHasMoreAgents(totalPages > 0 ? pageNum < totalPages : nextAgents.length === AGENTS_PAGE_SIZE);
    } catch {
      if (activeLoadIdRef.current !== loadId) return;

      if (!append) {
        setError("Failed to load available agents.");
        setAvailableAgents([]);
      } else {
        setError("Failed to load more available agents.");
      }
    } finally {
      if (activeLoadIdRef.current === loadId) {
        setLoadingAgents(false);
        setLoadingMoreAgents(false);
        isFetchingAgentsRef.current = false;
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    setAgentsPage(1);
    setAvailableAgents([]);
    setHasMoreAgents(true);
    loadAgents(1, false);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      activeLoadIdRef.current += 1;
      isFetchingAgentsRef.current = false;
    };
  }, [open, loadAgents]);

  const handleMenuScroll = React.useCallback((event) => {
    const menuPaper = event?.currentTarget;
    if (!menuPaper) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const isNearBottom =
        menuPaper.scrollTop + menuPaper.clientHeight >= menuPaper.scrollHeight - 160;

      if (
        !isNearBottom ||
        !hasMoreAgents ||
        loadingAgents ||
        loadingMoreAgents ||
        isFetchingAgentsRef.current
      ) {
        return;
      }

      const nextPage = agentsPage + 1;
      setAgentsPage(nextPage);
      loadAgents(nextPage, true);
    }, 150);
  }, [agentsPage, hasMoreAgents, loadingAgents, loadingMoreAgents, loadAgents]);

  const handleMenuOpen = React.useCallback(() => {
    if (
      !hasMoreAgents ||
      loadingAgents ||
      loadingMoreAgents ||
      selectableAgents.length > 0 ||
      isFetchingAgentsRef.current
    ) {
      return;
    }

    const nextPage = agentsPage + 1;
    setAgentsPage(nextPage);
    loadAgents(nextPage, true);
  }, [agentsPage, hasMoreAgents, loadingAgents, loadingMoreAgents, loadAgents, selectableAgents.length]);

  const addMember = () => {
    if (!selectedAgentId) return;
    const agent = agentById.get(String(selectedAgentId));
    if (!agent) return;

    setFormData((prev) => ({
      ...prev,
      members: [
        ...prev.members,
        {
          member_agent_id: agent.id,
          member_name: agent.name || `Agent ${agent.id}`,
          role: agent.agent_role || "",
          position: prev.members.length,
        },
      ],
    }));
    setSelectedAgentId("");
    setError("");
  };

  const updateMember = (index, patch) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members.map((member, memberIndex) =>
        memberIndex === index ? { ...member, ...patch } : member
      ),
    }));
  };

  const removeMember = (index) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members
        .filter((_, memberIndex) => memberIndex !== index)
        .map((member, position) => ({ ...member, position })),
    }));
  };

  const moveMember = (index, direction) => {
    setFormData((prev) => {
      const next = [...prev.members];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return {
        ...prev,
        members: next.map((member, position) => ({ ...member, position })),
      };
    });
  };

  const updateTeamMode = (teamMode) => {
    setFormData((prev) => ({
      ...prev,
      team_mode: teamMode,
      determine_input_for_members:
        teamMode === "broadcast" ? false : true,
    }));
  };

  const validate = () => {
    if (!isRequiredNumber(leaderAgentId)) {
      return "Save the leader agent before configuring a team.";
    }
    if (!TEAM_MODE_VALUES.includes(formData.team_mode)) {
      return "Select a valid team mode.";
    }
    if ((formData.name || "").trim().length > MAX_TEAM_NAME_LENGTH) {
      return `Team name must be ${MAX_TEAM_NAME_LENGTH} characters or less.`;
    }
    if (typeof formData.determine_input_for_members !== "boolean") {
      return "Determine input for members must be true or false.";
    }
    if (!Array.isArray(formData.members) || formData.members.length < 1) {
      return "Add at least one team member.";
    }

    const memberIds = [];
    for (let index = 0; index < formData.members.length; index += 1) {
      const member = formData.members[index];
      const memberNumber = Number(member.member_agent_id);
      const positionNumber = Number(member.position);

      if (!isRequiredNumber(member.member_agent_id)) {
        return `Member ${index + 1} must have a valid agent.`;
      }
      if ((member.role || "").trim().length > MAX_MEMBER_ROLE_LENGTH) {
        return `Member ${index + 1} role must be ${MAX_MEMBER_ROLE_LENGTH} characters or less.`;
      }
      if (!isRequiredNumber(member.position) || positionNumber < 0) {
        return `Member ${index + 1} position must be 0 or greater.`;
      }

      memberIds.push(memberNumber);
    }

    if (memberIds.some((id) => String(id) === String(Number(leaderAgentId)))) {
      return "The leader agent cannot also be a member.";
    }
    if (new Set(memberIds).size !== memberIds.length) {
      return "Each member agent can only appear once.";
    }
    return "";
  };

  const handleSave = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    onSave({
      diy_agent_id: Number(leaderAgentId),
      team_mode: formData.team_mode,
      name: formData.name.trim() || `${leaderName || "Agent"} Team`,
      description: formData.description.trim(),
      instructions: formData.instructions.trim(),
      determine_input_for_members:
        formData.team_mode === "broadcast" ? false : Boolean(formData.determine_input_for_members),
      members: formData.members.map((member, position) => ({
        member_agent_id: Number(member.member_agent_id),
        role: member.role?.trim() || "",
        position,
      })),
    });
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={() => !saving && onClose(null)}
        maxWidth="lg"
        fullWidth
        className={styles.dialog}
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Groups sx={{ color: "#e36250" }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Agent Team
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Leader: {leaderName || `Agent ${leaderAgentId}`}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => onClose(null)} disabled={saving} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Box className={styles.formContainer}>
            {error && <Alert severity="error">{error}</Alert>}

            <Box className={styles.section}>
              <Typography className={styles.sectionLabel}>Team details</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Team name"
                  fullWidth
                  value={formData.name}
                  onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                  inputProps={{ maxLength: MAX_TEAM_NAME_LENGTH }}
                />
                <TextField
                  label="Description"
                  fullWidth
                  value={formData.description}
                  onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
                />
              </Stack>
            </Box>

            <Box className={styles.section}>
              <Typography className={styles.sectionLabel}>Mode</Typography>
              <Box className={styles.modeGrid}>
                {TEAM_MODES.map((mode) => (
                  <Box
                    key={mode.value}
                    className={`${styles.modeCard} ${formData.team_mode === mode.value ? styles.modeCardActive : ""}`}
                    onClick={() => updateTeamMode(mode.value)}
                    role="button"
                    tabIndex={0}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                      {mode.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {mode.hint}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box className={styles.section}>
              <Typography className={styles.sectionLabel}>Team instructions</Typography>
              <TextField
                label="Optional leader guidance"
                value={formData.instructions}
                onChange={(event) => setFormData((prev) => ({ ...prev, instructions: event.target.value }))}
                multiline
                minRows={4}
                fullWidth
                helperText="Appended to the leader's instructions at runtime."
              />
            </Box>

            <Box className={styles.section}>
              <Box className={styles.sectionHeader}>
                <Typography className={styles.sectionLabel}>Members</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel>Add member</InputLabel>
                    <Select
                      label="Add member"
                      value={selectedAgentId}
                      onChange={(event) => setSelectedAgentId(event.target.value)}
                      onOpen={handleMenuOpen}
                      disabled={loadingAgents || (!hasMoreAgents && selectableAgents.length === 0)}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            overflow: "auto",
                          },
                          onScroll: handleMenuScroll,
                        },
                        disableScrollLock: true,
                      }}
                    >
                      {loadingAgents ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading agents...
                        </MenuItem>
                      ) : selectableAgents.length === 0 ? (
                        <MenuItem disabled>
                          {hasMoreAgents ? "Loading more agents..." : "No agents available"}
                        </MenuItem>
                      ) : (
                        selectableAgents.map((agent) => (
                          <MenuItem key={agent.id} value={agent.id}>
                            {agent.name || `Agent ${agent.id}`}
                          </MenuItem>
                        ))
                      )}
                      {loadingMoreAgents && (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading more agents...
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={loadingAgents ? <CircularProgress size={14} /> : <Add />}
                    onClick={addMember}
                    disabled={!selectedAgentId || loadingAgents}
                  >
                    Add
                  </Button>
                </Stack>
              </Box>

              {formData.members.length === 0 ? (
                <Box className={styles.emptyState}>
                  <Typography variant="body2">Add at least one member agent to save this team.</Typography>
                </Box>
              ) : (
                <Box className={styles.membersList}>
                  {formData.members.map((member, index) => (
                    <Box key={`${member.member_agent_id}-${index}`} className={styles.memberRow}>
                      <Box className={styles.memberPosition}>#{index + 1}</Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0 }}>
                        {member.member_name || agentById.get(String(member.member_agent_id))?.name || `Agent ${member.member_agent_id}`}
                      </Typography>
                      <TextField
                        size="small"
                        label="Role in this team"
                        value={member.role}
                        onChange={(event) => updateMember(index, { role: event.target.value })}
                        fullWidth
                        inputProps={{ maxLength: MAX_MEMBER_ROLE_LENGTH }}
                      />
                      <Box className={styles.memberActions}>
                        <Tooltip title="Move up" arrow>
                          <span>
                            <IconButton size="small" onClick={() => moveMember(index, -1)} disabled={index === 0}>
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down" arrow>
                          <span>
                            <IconButton size="small" onClick={() => moveMember(index, 1)} disabled={index === formData.members.length - 1}>
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove member" arrow>
                          <IconButton size="small" onClick={() => removeMember(index)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box className={styles.advancedBox}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.determine_input_for_members}
                    disabled={formData.team_mode === "broadcast"}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        determine_input_for_members: event.target.checked,
                      }))
                    }
                  />
                }
                label="Determine input for members"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {formData.team_mode === "broadcast"
                  ? "Broadcast sends the original user query to every member, so input determination is disabled."
                  : "Keep enabled unless every member should receive the original user query unchanged. When Human-in-the-Loop is on, members run by position."}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {initialData?.id ? "Save Team" : "Create Team"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AgentTeamDialog;
